import { applyAnswerUpdates } from './answerUpdateStrategies'
import { removeAnswerFromActivityGroupAnswers } from '../../helpers/clearAnswerUtils'
import getReducerHandler from '../../providers/getReducerHandler'
import handleReducerSimpleSetter from '../../providers/handleReducerSimpleSetter'

// Exported action constants to avoid string literal typos across dispatch sites
export const ACTIONS = Object.freeze({
  SET_ACTIVITYGROUP_MODE: 'SET_ACTIVITYGROUP_MODE',
  CLEAR_ANSWER: 'CLEAR_ANSWER',
  CLEAR_ANSWERS: 'CLEAR_ANSWERS',
  CLEAR_ACTIVITY_GROUP: 'CLEAR_ACTIVITY_GROUP',
  UPDATE_ANSWER: 'UPDATE_ANSWER',
  UPDATE_ACTIVITY_GROUP_ANSWER_ID: 'UPDATE_ACTIVITY_GROUP_ANSWER_ID',
  SET_REQUESTED_ACTIVITY_GROUP_ID: 'SET_REQUESTED_ACTIVITY_GROUP_ID',
  SET_IS_ACTIVITY_GROUP_OPEN: 'SET_IS_ACTIVITY_GROUP_OPEN',
  SET_NEXT_ENABLED_FROM_INDEX: 'SET_NEXT_ENABLED_FROM_INDEX',
})

const resetBaseActivityGroupProperties = () => ({
  activities: [],
  activityGroupAnswerId: null,
  activityGroupAnswers: null,
  activityGroupMode: null,
  activeIndex: 0,
  memoryArtifact: null,
  requestedActivityGroupId: null,
  requestedAnswerOwnerCisId: null,
})

/*
 * --- INDIVIDUAL ACTION HANDLERS ---
 * (Set multiple values & merge/processing logic only, otherwise use handleReducerSimpleSetter)
 */

const handleSetActivityGroupMode = (state, action) => ({
  // Only reset answers when switching to a *different* mode. Re-dispatching the same mode previously caused
  // a jarring flash + re-fetch because we nulled out existing answers just before viewing them.
  ...(state.activityGroupMode === action.activityGroupMode
    ? state
    : {
        ...state,
        activityGroupMode: action.activityGroupMode,
        activityGroupAnswerId: null,
        activityGroupAnswers: null,
      }),
})

const handleClearAnswer = (state, action) => {
  const { activityId, type, indexAGA, baseActivityGroupAnswers } = action?.value || {}
  let activityGroupAnswers
  if (state.activityGroupAnswers) activityGroupAnswers = { ...state.activityGroupAnswers }
  else if (baseActivityGroupAnswers) activityGroupAnswers = { ...baseActivityGroupAnswers }
  else activityGroupAnswers = null
  activityGroupAnswers = removeAnswerFromActivityGroupAnswers({
    activityGroupAnswers,
    type,
    activityId,
    indexAGA,
  })
  return { ...state, activityGroupAnswers }
}

const handleClearAnswers = (state) => ({ ...state, activityGroupAnswers: null })

const handleClearActivityGroup = (state) => ({ ...state, ...resetBaseActivityGroupProperties() })

const handleUpdateAnswer = (state, action) => {
  const {
    title = '',
    answers = [],
    activityGroupAnswerId = state?.activityGroupAnswerId,
    baseActivityGroupAnswers,
  } = action?.value || {}
  let working
  if (state.activityGroupAnswers) working = { ...state.activityGroupAnswers }
  else if (baseActivityGroupAnswers) working = { ...baseActivityGroupAnswers }
  else working = {}
  if (title) working.title = title
  working = applyAnswerUpdates(working, answers)
  return { ...state, activityGroupAnswers: { ...working }, activityGroupAnswerId }
}

const handleUpdateActivityGroupAnswerId = (state, action) => {
  let valueToSet = null
  const raw = action.value.activityGroupAnswerId
  if (typeof raw === 'number') valueToSet = raw
  else if (typeof raw === 'string') valueToSet = parseInt(raw, 10)
  return {
    ...state,
    activityGroupAnswerId: typeof valueToSet === 'number' && !Number.isNaN(valueToSet) ? valueToSet : null,
    requestedAnswerOwnerCisId: action.value.ownerCisId,
  }
}

const handleSetIsActivityGroupOpen = (state, action) =>
  action.value.isActivityGroupOpen
    ? {
        ...state,
        isActivityGroupOpen: action.value.isActivityGroupOpen,
        requestedActivityGroupId: action.value.activityGroupId ?? state.requestedActivityGroupId,
        activeIndex: action.value.activeIndex ?? 0,
      }
    : {
        ...state,
        ...resetBaseActivityGroupProperties(),
        isActivityGroupOpen: false,
      }

// --- Handlers registry ---
const handlers = {
  [ACTIONS.SET_ACTIVITYGROUP_MODE]: handleSetActivityGroupMode,
  [ACTIONS.CLEAR_ANSWER]: handleClearAnswer,
  [ACTIONS.CLEAR_ANSWERS]: handleClearAnswers,
  [ACTIONS.CLEAR_ACTIVITY_GROUP]: handleClearActivityGroup,
  [ACTIONS.UPDATE_ANSWER]: handleUpdateAnswer,
  [ACTIONS.UPDATE_ACTIVITY_GROUP_ANSWER_ID]: handleUpdateActivityGroupAnswerId,
  [ACTIONS.SET_REQUESTED_ACTIVITY_GROUP_ID]: handleReducerSimpleSetter,
  [ACTIONS.SET_IS_ACTIVITY_GROUP_OPEN]: handleSetIsActivityGroupOpen,
  [ACTIONS.SET_NEXT_ENABLED_FROM_INDEX]: handleReducerSimpleSetter,
}

export default function activityGroupReducer(state, action) {
  return getReducerHandler(state, action, handlers)
}
